import React from 'react';
import { IntegrationProps } from '../../../../types';

export const ShopifySettings: React.FC<IntegrationProps> = ({ config, onChange }) => {
  // Helper to strip the suffix for display purposes
  const getDisplayDomain = (fullDomain: string) => {
    if (!fullDomain) return '';
    return fullDomain.replace('.myshopify.com', '');
  };

  const handleDomainChange = (val: string) => {
    // Automatically strip special chars and suffix if user pastes it
    const cleanVal = val.replace(/https?:\/\//, '').replace('.myshopify.com', '').trim();
    // Always save with the suffix
    onChange({ domain: `${cleanVal}.myshopify.com` });
  };

  return (
    <div className="space-y-4">
      {/* Info Banner */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
        <p className="text-sm text-blue-800">
          <strong>Note:</strong> Shopify now uses Client Credentials Grant for custom apps. 
          Find your credentials in <strong>Dev Dashboard → Your App → Settings</strong>.
        </p>
      </div>

      {/* 1. Shop Domain */}
      <div>
        <label className="block text-sm font-semibold text-slate-700 mb-1">
          Shopify Store Domain
        </label>
        <div className="flex rounded-lg shadow-sm">
          <input
            type="text"
            value={getDisplayDomain(config.domain)}
            onChange={(e) => handleDomainChange(e.target.value)}
            className="flex-1 min-w-0 block w-full px-3 py-2.5 rounded-l-lg border border-slate-300 focus:ring-2 focus:ring-blue-500 outline-none"
            placeholder="your-store-name"
          />
          <span className="inline-flex items-center px-3 rounded-r-lg border border-l-0 border-slate-300 bg-slate-50 text-slate-500 text-sm">
            .myshopify.com
          </span>
        </div>
        <p className="text-xs text-slate-500 mt-1">
          Enter your store name (e.g., if your URL is <b>mystore.myshopify.com</b>, enter <b>mystore</b>).
        </p>
      </div>

      {/* 2. Client ID */}
      <div>
        <label className="block text-sm font-semibold text-slate-700 mb-1">
          Client ID
        </label>
        <input
          type="text"
          value={config.clientId || ''}
          onChange={(e) => onChange({ clientId: e.target.value.trim() })}
          className="w-full p-2.5 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none font-mono text-sm"
          placeholder="599a85316d3d8de1cee025e5d87481dd"
        />
        <p className="text-xs text-slate-500 mt-1">
          Found in <b>Dev Dashboard → App Settings → Credentials</b>
        </p>
      </div>

      {/* 3. Client Secret */}
      <div>
        <label className="block text-sm font-semibold text-slate-700 mb-1">
          Client Secret
        </label>
        <input
          type="password"
          value={config.clientSecret || ''}
          onChange={(e) => onChange({ clientSecret: e.target.value.trim() })}
          className="w-full p-2.5 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none font-mono text-sm"
          placeholder="shpss_xxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx"
        />
        <p className="text-xs text-slate-500 mt-1">
          Click "Reveal" in Dev Dashboard to copy your secret. <span className="text-amber-600">Keep this secure!</span>
        </p>
      </div>

      {/* Required Scopes Info */}
      <div className="bg-slate-50 border border-slate-200 rounded-lg p-3 mt-4">
        <p className="text-sm font-medium text-slate-700 mb-2">Required API Scopes:</p>
        <div className="flex flex-wrap gap-2">
          {['read_orders', 'read_customers', 'read_products', 'write_products'].map((scope) => (
            <span 
              key={scope}
              className="px-2 py-1 bg-slate-200 text-slate-700 text-xs rounded font-mono"
            >
              {scope}
            </span>
          ))}
        </div>
        <p className="text-xs text-slate-500 mt-2">
          Ensure these scopes are enabled in your app's configuration. If you add new scopes, reinstall the app on your store.
        </p>
      </div>
    </div>
  );
};
